import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilePdf, 
  faUpload, 
  faTrash, 
  faSpinner,
  faPlus,
  faPenToSquare,
  faFileLines,
  faEye,
  faBuilding,
  faExclamationTriangle,
  faCheck,
  faCircle
} from '@fortawesome/free-solid-svg-icons';
import styles from '../ManagerDashboard/Profile.module.css';

const EPKSection = ({ 
  profileData, 
  isOwnProfile, 
  handleEPKView, 
  handleEPKUpload, 
  handleEPKDelete,
  openEPKBuilder,
  customEPK,
  updateEPKPreference // New prop to handle updating the preference
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [selectedEPKType, setSelectedEPKType] = useState('custom'); // 'custom' or 'pdf'
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const fileInputRef = useRef(null);
  
  // Set default selected EPK type based on user's stored preference
  useEffect(() => {
    // If user has a stored preference, use that
    if (profileData?.preferredEPKType) {
      setSelectedEPKType(profileData.preferredEPKType);
    // Otherwise, set based on what's available
    } else if (customEPK?.isActive) {
      setSelectedEPKType('custom');
    } else if (profileData?.epk) {
      setSelectedEPKType('pdf');
    }
  }, [customEPK, profileData]);
  
  // Add debugging for customEPK
  useEffect(() => {
    console.log('EPKSection received customEPK:', customEPK);
  }, [customEPK]);
  
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // Handle EPK preference change and persist to database
  const handleEPKPreferenceChange = async (type) => {
    if (type === selectedEPKType) return;
    
    setIsSavingPreference(true);
    try {
      // Update local state first (optimistic update)
      setSelectedEPKType(type);
      
      // Call parent component's method to update the preference in the database
      if (updateEPKPreference) {
        await updateEPKPreference(type);
      }
    } catch (error) {
      console.error('Error updating EPK preference:', error);
      // Revert to previous selection on error
      setSelectedEPKType(selectedEPKType);
      alert('Failed to update your EPK preference. Please try again.');
    } finally {
      setIsSavingPreference(false);
    }
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit. Please select a smaller file.');
      return;
    }
    
    // Validate file type (only PDF)
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      return;
    }
    
    // Start upload process with simulated progress for better UX
    setIsUploading(true);
    setUploadProgress(10);
    setUploadError(null);
    
    // Simulate progress while actual upload happens
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const next = prev + Math.floor(Math.random() * 10);
        return next < 90 ? next : prev;
      });
    }, 500);
    
    try {
      // Call the parent's handleEPKUpload and let it do the uploading
      await handleEPKUpload(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Set the selected type to PDF after successful upload
      setSelectedEPKType('pdf');
      
      // Also update the preference in the database
      if (updateEPKPreference) {
        await updateEPKPreference('pdf');
      }
      
      // Reset progress after a short delay
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error uploading EPK:', error);
      setUploadError(error.message || 'Failed to upload EPK');
      setUploadProgress(0);
      setIsUploading(false);
    } finally {
      // Reset the file input
      e.target.value = '';
    }
  };
  
  // Determine what to display based on user's EPK status
  const hasUploadedEPK = profileData?.epk;
  const hasCustomEPK = customEPK && customEPK.isActive === true;
  
  // Both types are available
  const hasBothEPKTypes = hasUploadedEPK && hasCustomEPK;
  
  // Debug logging
  console.log('EPK Display Status:', { 
    hasUploadedEPK: !!hasUploadedEPK, 
    hasCustomEPK: !!hasCustomEPK,
    hasBothEPKTypes,
    selectedEPKType,
    customEPK
  });
  
  // For non-owners, just show the preferred EPK type
  // If no preference is set, show custom EPK if available, otherwise PDF
  const epkTypeToShow = isOwnProfile 
    ? selectedEPKType 
    : (profileData?.preferredEPKType || (hasCustomEPK ? 'custom' : 'pdf'));
  
  return (
    <div className={styles.associatedActsSection}>
      <h3 className={styles.sectionTitle}>Electronic Press Kit (EPK)</h3>
      
      {(hasUploadedEPK || hasCustomEPK) ? (
        // User has an EPK - show the available EPK(s)
        <div className={styles.epkContainer}>
          {/* EPK Type Toggle when both types are available - only for owners */}
          {hasBothEPKTypes && isOwnProfile && (
            <div className={styles.epkVisibilityToggle}>
              <p className={styles.epkVisibilityLabel}>
                <FontAwesomeIcon icon={faEye} /> 
                Choose which EPK others will see:
              </p>
              
              <div className={styles.epkVisibilityOptions}>
                <label className={`${styles.epkVisibilityOption} ${selectedEPKType === 'custom' ? styles.epkVisibilitySelected : ''}`}>
                  <input 
                    type="radio" 
                    name="epkType" 
                    value="custom"
                    checked={selectedEPKType === 'custom'} 
                    onChange={() => handleEPKPreferenceChange('custom')}
                    className={styles.epkVisibilityRadio}
                    disabled={isSavingPreference}
                  />
                  <div className={styles.epkVisibilityRadioCustom}>
                    {selectedEPKType === 'custom' ? (
                      <FontAwesomeIcon icon={faCheck} className={styles.epkVisibilityIcon} />
                    ) : (
                      <FontAwesomeIcon icon={faCircle} className={styles.epkVisibilityIcon} />
                    )}
                  </div>
                  <div className={styles.epkVisibilityContent}>
                    <FontAwesomeIcon icon={faFileLines} className={styles.epkVisibilityTypeIcon} />
                    <span>Built EPK</span>
                  </div>
                </label>
                
                <label className={`${styles.epkVisibilityOption} ${selectedEPKType === 'pdf' ? styles.epkVisibilitySelected : ''}`}>
                  <input 
                    type="radio" 
                    name="epkType" 
                    value="pdf"
                    checked={selectedEPKType === 'pdf'} 
                    onChange={() => handleEPKPreferenceChange('pdf')}
                    className={styles.epkVisibilityRadio}
                    disabled={isSavingPreference}
                  />
                  <div className={styles.epkVisibilityRadioCustom}>
                    {selectedEPKType === 'pdf' ? (
                      <FontAwesomeIcon icon={faCheck} className={styles.epkVisibilityIcon} />
                    ) : (
                      <FontAwesomeIcon icon={faCircle} className={styles.epkVisibilityIcon} />
                    )}
                  </div>
                  <div className={styles.epkVisibilityContent}>
                    <FontAwesomeIcon icon={faFilePdf} className={styles.epkVisibilityTypeIcon} />
                    <span>PDF EPK</span>
                  </div>
                </label>
              </div>
              
              {isSavingPreference && (
                <p className={styles.epkVisibilitySaving}>
                  <FontAwesomeIcon icon={faSpinner} spin /> 
                  Saving preference...
                </p>
              )}
            </div>
          )}
          
          {/* Display PDF EPK if it's the selected type to show */}
          {hasUploadedEPK && (isOwnProfile ? (selectedEPKType === 'pdf') : (epkTypeToShow === 'pdf')) && (
            <div 
              className={styles.epkItem}
              onClick={() => handleEPKView(profileData.epk)}
            >
              <FontAwesomeIcon icon={faFilePdf} className={styles.epkIcon} />
              <div className={styles.epkInfo}>
                <h4 className={styles.epkName}>{profileData.epk.fileName || 'Press Kit'}</h4>
                <p className={styles.epkNote}>Click to view PDF</p>
              </div>
            </div>
          )}
          
          {/* Display custom built EPK if it's the selected type to show */}
          {hasCustomEPK && (isOwnProfile ? (selectedEPKType === 'custom') : (epkTypeToShow === 'custom')) && (
            <div 
              className={`${styles.epkItem} ${styles.customEpkItem}`}
              onClick={() => openEPKBuilder('view')}
            >
              <FontAwesomeIcon icon={faFileLines} className={styles.epkIcon} />
              <div className={styles.epkInfo}>
                <h4 className={styles.epkName}>
                  {customEPK.title || 'Custom Press Kit'}
                  <span className={styles.customEpkBadge}>
                    <FontAwesomeIcon icon={faBuilding} />
                    Built with EPK Builder
                  </span>
                </h4>
                <p className={styles.epkNote}>Click to view</p>
              </div>
            </div>
          )}
          
          {/* Owner actions for EPK management */}
          {isOwnProfile && (
            <div className={styles.epkActions}>
              <div className={styles.epkButtonGroup}>
                {hasUploadedEPK ? (
                  <>
                    <button 
                      className={styles.uploadButton}
                      onClick={triggerFileInput}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faUpload} />
                      )}
                      Replace PDF
                    </button>
                    
                    <button 
                      className={`${styles.uploadButton} ${styles.deleteButton}`}
                      onClick={() => handleEPKDelete(profileData.epk)}
                      disabled={isUploading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Remove
                    </button>
                  </>
                ) : (
                  <button 
                    className={styles.uploadButton}
                    onClick={triggerFileInput}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faUpload} />
                    )}
                    Upload PDF EPK
                  </button>
                )}
                
                {hasCustomEPK ? (
                  <button 
                    className={styles.uploadButton}
                    onClick={() => openEPKBuilder('edit')}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                    Edit Built EPK
                  </button>
                ) : (
                  <button 
                    className={styles.uploadButton}
                    onClick={() => openEPKBuilder('create')}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Create with EPK Builder
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Upload Progress */}
          {isUploading && uploadProgress > 0 && (
            <div className={styles.uploadProgressContainer}>
              <div className={styles.uploadProgressBar}>
                <div 
                  className={styles.uploadProgressFill} 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className={styles.uploadProgressText}>
                Uploading PDF... {uploadProgress}%
              </div>
            </div>
          )}
          
          {/* Upload Error */}
          {uploadError && (
            <div className={styles.uploadError}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{uploadError}</span>
            </div>
          )}
        </div>
      ) : (
        // No EPK yet - show options for owner, empty state for visitors
        isOwnProfile ? (
          <div className={styles.epkOptionsContainer}>
            <div className={styles.epkOptionItems}>
              <div 
                className={styles.epkOptionCard}
                onClick={triggerFileInput}
              >
                <div className={styles.epkOptionIcon}>
                  <FontAwesomeIcon icon={faFilePdf} />
                </div>
                <h4>Upload PDF</h4>
                <p>Upload your existing press kit PDF</p>
              </div>
              
              <div 
                className={styles.epkOptionCard}
                onClick={() => openEPKBuilder('create')}
              >
                <div className={styles.epkOptionIcon}>
                  <FontAwesomeIcon icon={faFileLines} />
                </div>
                <h4>EPK Builder</h4>
                <p>Create a professional EPK online</p>
              </div>
            </div>
            
            <p className={styles.epkOptionNote}>
              An Electronic Press Kit (EPK) helps event organizers, venues, and media learn about you
            </p>
            
            {/* Upload Progress when no existing EPK */}
            {isUploading && uploadProgress > 0 && (
              <div className={styles.uploadProgressContainer}>
                <div className={styles.uploadProgressBar}>
                  <div 
                    className={styles.uploadProgressFill} 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className={styles.uploadProgressText}>
                  Uploading PDF... {uploadProgress}%
                </div>
              </div>
            )}
            
            {/* Upload Error */}
            {uploadError && (
              <div className={styles.uploadError}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{uploadError}</span>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No press kit available</p>
          </div>
        )
      )}
      
      {/* Hidden file input for PDF upload */}
      <input
        type="file"
        accept=".pdf"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default EPKSection;